import { intervalDuration, runningBpm } from "../lib/runningTraining";
import { RUNNING_TRAINING_CHOICES, runningChoiceLabelKey, runningTrainingChoice } from "../lib/runningTrainingSelection";
import { runningActivitySelection } from "./CalendarActivityFields";

function RunningField({ label, children }) {
  return <label className="block min-w-0"><span className="block text-[11px] mb-1.5" style={{ color: "var(--pc-muted)" }}>{label}</span>{children}</label>;
}

export default function RunningTrainingFields({ editor, onChange, disabled = false, variantLocked = false, allowMeasuredHrmax = true, t }) {
  const details = editor.running_details || {};
  const variant = runningTrainingChoice(editor);
  const structured = Boolean(editor.training_template);
  const sprint = variant === "sprint_acceleration";
  const interval = variant === "interval_run";
  const bpm = allowMeasuredHrmax ? runningBpm(details, variant) : null;
  const updateDetail = (key, value) => {
    const next = { ...details, [key]: value };
    const patch = { running_details: next };
    if (interval) {
      const complete = ["warmup_minutes", "cooldown_minutes", "repetitions", "work_minutes", "rest_minutes"]
        .every((field) => next[field] !== "" && next[field] != null);
      patch.duration = complete ? intervalDuration(next) : "";
    }
    onChange(patch);
  };
  const numberField = (key, label, min, max, step = 1, optional = false) => <RunningField key={key} label={t(label)}>
    <input className="pc-input w-full min-w-0" data-testid={`running-${key}`} type="number" min={min} max={max} step={step}
      disabled={disabled} value={details[key] ?? ""} onChange={(event) => updateDetail(key, event.target.value === "" ? (optional ? null : "") : Number(event.target.value))} />
  </RunningField>;
  return <fieldset className="min-w-0 rounded-xl p-3 space-y-3" disabled={disabled} data-testid="running-training-fields"
    style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }}>
    <legend className="text-xs font-semibold px-1">{t("trainingUnitType_running_training")}</legend>
    <RunningField label={t("runningVariant")}><select className="pc-input" data-testid="running-variant" disabled={variantLocked} value={variant} onChange={(event) => {
      const preset = runningActivitySelection(event.target.value, t, editor);
      if (preset.running_details && allowMeasuredHrmax && event.target.value !== "sprint_acceleration" && details.measured_hrmax) preset.running_details.measured_hrmax = details.measured_hrmax;
      onChange(preset);
    }}><option value="" disabled>{t("pleaseSelect")}</option>{RUNNING_TRAINING_CHOICES.map((value) => <option key={value} value={value}>{t(runningChoiceLabelKey(value))}</option>)}</select></RunningField>
    {!structured && <>
    {variant && <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t(`runningRecommended_${variant}`)}</p>}
    <RunningField label={t("runningIntensity")}><input className="pc-input" data-testid="running-intensity" value={details.intensity || ""} maxLength={240} onChange={(event) => updateDetail("intensity", event.target.value)} /></RunningField>
    <fieldset className="space-y-2" data-testid="running-planned-exertion">
      <legend className="text-xs mb-2">{t("runningPlannedExertion")}</legend>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((value) => <button key={value} type="button" className="pc-btn min-w-0 px-0"
          data-testid={`running-planned-exertion-${value}`} aria-pressed={details.planned_exertion === value}
          aria-label={`${value} – ${t(`runningLoad_${value}`)}`}
          style={details.planned_exertion === value ? { color: "var(--pc-cyan)", borderColor: "var(--pc-cyan)" } : undefined}
          onClick={() => updateDetail("planned_exertion", value)}>{value}</button>)}
      </div>
      <p className="text-xs" aria-live="polite" style={{ color: "var(--pc-muted)" }}>
        {[1, 2, 3, 4, 5].includes(details.planned_exertion)
          ? `${details.planned_exertion} – ${t(`runningLoad_${details.planned_exertion}`)}` : t("runningLegacyLoadHint")}
      </p>
    </fieldset>
    {!sprint && <>
      <RunningField label={t("runningHrZone")}><input className="pc-input" data-testid="running-hr_zone" value={details.hr_zone || ""} maxLength={3} onChange={(event) => updateDetail("hr_zone", event.target.value)} /></RunningField>
      <div className="grid grid-cols-2 gap-2">
        {numberField("hr_percent_min", "runningHrPercentMin", 1, 100)}
        {numberField("hr_percent_max", "runningHrPercentMax", 1, 100)}
      </div>
      {allowMeasuredHrmax && <>{numberField("measured_hrmax", "runningMeasuredHrmax", 80, 240, 1, true)}
        <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("runningMeasuredHrmaxHint")}</p></>}
      {bpm && bpm.every(Number.isFinite) && <p data-testid="running-bpm" className="text-xs font-semibold" style={{ color: "var(--pc-cyan)" }}>{t("runningBpm")}: {bpm[0]}–{bpm[1]}</p>}
    </>}
    {(interval || sprint) && <div className="grid grid-cols-2 gap-2">
      {numberField("repetitions", "runningRepetitions", 1, 100)}
      {numberField("warmup_minutes", "runningWarmupMinutes", 1, 120)}
      {interval && numberField("work_minutes", "runningWorkMinutes", 0.1, 120, 0.1)}
      {interval && numberField("rest_minutes", "runningRestMinutes", 0, 120, 0.1)}
      {interval && numberField("cooldown_minutes", "runningCooldownMinutes", 0, 120)}
      {sprint && numberField("distance_m", "runningDistanceM", 1, 1000)}
      {sprint && numberField("rest_seconds", "runningRestSeconds", 1, 600)}
    </div>}
    {interval && <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("runningIntervalTotalHint")}</p>}
    {sprint && <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("runningSprintHint")}</p>}
    <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("runningOrientation")}</p>
    </>}
  </fieldset>;
}
